import type { FloorData } from 'store/building/types';

export interface FloorSuiteOption {
  floor: string;
  suite?: string;
  displayLabel: string;
  value: string;
}

/**
 * Build the list of selectable floor/suite options for the host step dropdown.
 *
 * - Skips floors whose `number` is missing or blank.
 * - When a floor has at least one non-blank suite, emits one option per non-blank suite
 *   (label: "{floor} - {suite}", value: "{floor}|{suite}").
 * - When a floor has no usable suites, emits a single floor-only option
 *   (label and value: the floor number).
 *
 * Use this single source of truth so the render guard and the validation schema
 * agree on whether the user actually has anything to pick from.
 */
export const getFloorSuiteOptions = (floors: FloorData[] | null | undefined): FloorSuiteOption[] => {
  if (!floors || floors.length === 0) {
    return [];
  }

  const options: FloorSuiteOption[] = [];

  floors.forEach((floorData) => {
    if (!floorData.number || floorData.number.trim() === '') {
      return;
    }

    const validSuites = (floorData.suites ?? []).filter((suiteName) => !!suiteName && suiteName.trim() !== '');

    if (validSuites.length > 0) {
      validSuites.forEach((suiteName) => {
        options.push({
          floor: floorData.number,
          suite: suiteName,
          displayLabel: `${floorData.number} - ${suiteName}`,
          value: `${floorData.number}|${suiteName}`,
        });
      });
    } else {
      options.push({
        floor: floorData.number,
        suite: undefined,
        displayLabel: floorData.number,
        value: floorData.number,
      });
    }
  });

  return options;
};
